import {useCallback, useEffect, useMemo, useState} from 'react';
import {Button} from '@ezcater/tapas';
import {addMinutes, format, parse} from 'date-fns';
import {useFormikContext} from 'formik';
import {twJoin, twMerge} from 'tailwind-merge';
import {Temporal} from 'temporal-polyfill';

import {ezoTimePickerSelectedStyles} from '@/components/features/menu/utils/ezoButtonStyles';
import {useSearchPageData} from '@/components/features/search/providers/SearchPageProvider';
import {useGoogleMapsGeocode} from '@/hooks/useGoogleMapsGeocode';
import useIdentity from '@/hooks/useIdentity';
import useOrderingExperience from '@/hooks/useOrderingExperience';
import {isEventTimeInvalid} from './utils';

// EventBarTimePicker is used in two places:
// - the full AppBar EventBar (which includes addressString)
// - the standalone EventBarDateTime on menu (which only has eventDate and eventTime).
type TimePickerFormValues = {
  addressString?: string;
  eventDate?: string | null;
};

const START_TIME = '5:00 AM';
const START_TIME_ADMIN = '12:00 AM';
const END_TIME = '11:30 PM';
const END_TIME_ADMIN = '11:45 PM';
const INTERVAL = 15;

const getTimeOptions = (isAdmin: boolean) => {
  const start = parse(isAdmin ? START_TIME_ADMIN : START_TIME, 'h:mm a', new Date());
  const end = parse(isAdmin ? END_TIME_ADMIN : END_TIME, 'h:mm a', new Date());
  const timeOptions = [];

  let currentTime = start;
  while (currentTime <= end) {
    timeOptions.push(format(currentTime, 'h:mm a'));
    currentTime = addMinutes(currentTime, INTERVAL);
  }

  return timeOptions;
};

type EventBarTimePickerProps = React.ComponentProps<'div'> & {
  allowDeselectTime?: boolean;
  onSelectTime: (time: string) => void;
  selectedTime?: string;
  timesPerPage?: number;
  initialShowIndex?: number;
};

const EventBarTimePicker: React.FC<EventBarTimePickerProps> = ({
  allowDeselectTime = false,
  onSelectTime,
  selectedTime,
  timesPerPage = 12,
  initialShowIndex = 24,
  ...props
}) => {
  const {isAdmin} = useIdentity();
  const {isEzOrdering} = useOrderingExperience();
  const timeOptions = useMemo(() => getTimeOptions(isAdmin), [isAdmin]);
  const selectedTimeIndex = timeOptions.findIndex(time => time === selectedTime);
  const pageIndex = Math.floor(selectedTimeIndex / timesPerPage) * timesPerPage;
  const [showIndex, setShowIndex] = useState(
    selectedTime ? pageIndex : isAdmin ? 48 : initialShowIndex,
  ); // selected page or noon

  const {searchPageData} = useSearchPageData();
  const {fullAddress: fullFDAddress} = searchPageData?.address ?? {fullAddress: ''};
  const [geoTZID, setGeoTZID] = useState<string | undefined>(
    searchPageData?.address.timeZoneIdentifier,
  );
  const {values} = useFormikContext<TimePickerFormValues>();
  const addressString = values.addressString;
  const eventDate = values.eventDate ?? null;
  const {fetchGeocodedAddress} = useGoogleMapsGeocode();
  const timeZone = geoTZID ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const temporalNow = Temporal.Now.instant().toZonedDateTimeISO(timeZone);

  const isTimeInvalid = useCallback(
    (
      timeToCheck: string | undefined,
      eventDate: string | null,
      temporalNow: Temporal.ZonedDateTime,
      isAdmin: boolean,
    ): boolean => {
      if (!timeToCheck) return false;

      const temporalSelectedTime = Temporal.PlainTime.from(
        format(parse(timeToCheck, 'h:mm a', new Date()), 'HH:mm:ss'),
      );
      return isEventTimeInvalid(temporalSelectedTime, eventDate, temporalNow, isAdmin);
    },
    [],
  );

  useEffect(() => {
    if (!isAdmin && addressString && addressString !== fullFDAddress) {
      const fetchTZID = async () => {
        const geoAddress = await fetchGeocodedAddress(addressString);
        if (geoAddress.length > 0) {
          setGeoTZID(geoAddress[0].timeZoneIdentifier ?? undefined);
        }
      };
      fetchTZID();
    }
  }, [addressString, fullFDAddress, isAdmin, fetchGeocodedAddress]);

  useEffect(() => {
    const isInvalid = isTimeInvalid(selectedTime, eventDate, temporalNow, isAdmin);
    if (isInvalid) {
      onSelectTime('');
    }
  }, [isAdmin, selectedTime, eventDate, temporalNow, onSelectTime, isTimeInvalid]);

  return (
    <div {...props} className={twMerge('flex w-[380px] flex-col gap-2', props?.className)}>
      <Button
        className="select-none border-peppercorn-200 font-normal"
        disabled={showIndex <= 0}
        onClick={() => setShowIndex(prev => prev - timesPerPage)}
        size="small"
        variant="outlined"
      >
        Earlier
      </Button>

      <div className="grid grid-cols-4 gap-2">
        {timeOptions.slice(showIndex, showIndex + timesPerPage).map(time => {
          const isSelected = selectedTime === time;
          const invalidTime = isTimeInvalid(time, eventDate, temporalNow, isAdmin);

          return (
            <Button
              disabled={!isAdmin && invalidTime}
              key={time}
              className={twJoin(
                'font-normal',
                isSelected && 'font-bold',
                isSelected
                  ? isEzOrdering
                    ? ezoTimePickerSelectedStyles
                    : 'border-green-700'
                  : 'border-peppercorn-200',
              )}
              onClick={() => {
                // app bar step-through experience
                if (allowDeselectTime) {
                  if (isSelected) {
                    onSelectTime('');
                  } else {
                    onSelectTime(time === 'Noon' ? '12:00 PM' : time);
                  }
                  return;
                }
                if (!isSelected) onSelectTime(time === 'Noon' ? '12:00 PM' : time);
              }}
              size="small"
              variant={isSelected ? 'primary' : 'outlined'}
            >
              {time === '12:00 PM' ? 'Noon' : time}
            </Button>
          );
        })}
      </div>

      <Button
        className="select-none border-peppercorn-200 font-normal"
        disabled={showIndex + timesPerPage >= timeOptions.length}
        onClick={() => setShowIndex(prev => prev + timesPerPage)}
        size="small"
        variant="outlined"
      >
        Later
      </Button>
    </div>
  );
};

export default EventBarTimePicker;
